/**
 * Seat layout helpers — the single source of truth for which seat numbers
 * exist in a case's grid and in what display order.
 *
 * The grid is a sequence of "spaces" (1..size+spacers; spacers only exist in
 * jury-box mode). Each non-spacer space has an effective seat number: the
 * user's custom number for that space (case.customSeatNumbers[mode]) or the
 * default (space index minus preceding spacers). Custom numbers let any
 * courtroom layout be mimicked on screen.
 *
 * Jurors store the effective seat number in seatNumber/alternateSeatNumber;
 * grid cells match jurors by that number. Batch actions (fill, slide,
 * restore) iterate the layout in display order rather than assuming seats
 * are numbered 1..N.
 */

export function getCustomSeatMap(caseData, mode = 'jury-box') {
  return caseData?.customSeatNumbers?.[mode] || {};
}

/**
 * @returns {Array<{spaceNumber: number, seatNumber: number, isCustom: boolean}>}
 *   Non-spacer seats in display (space) order.
 */
export function getSeatLayout(caseData, mode = 'jury-box') {
  const size =
    (mode === 'jury-box' ? caseData?.juryBoxSize : caseData?.numberOfAlternates) || 0;
  const spacers = mode === 'jury-box' ? caseData?.spacers || [] : [];
  const custom = getCustomSeatMap(caseData, mode);

  return buildSeatLayout(size, spacers, custom);
}

/**
 * Layout construction from raw parts, for callers that don't have the case
 * object at hand (e.g. the per-row rendering hook).
 */
export function buildSeatLayout(size, spacers = [], custom = {}) {
  const spacerSet = new Set(spacers);
  const layout = [];
  let spacersSeen = 0;
  const totalSpaces = size + spacers.length;

  for (let space = 1; space <= totalSpaces; space++) {
    if (spacerSet.has(space)) {
      spacersSeen++;
      continue;
    }
    const defaultSeat = space - spacersSeen;
    if (defaultSeat > size) break;

    const customVal = custom[space] ?? custom[String(space)];
    layout.push({
      spaceNumber: space,
      seatNumber: customVal != null ? Number(customVal) : defaultSeat,
      defaultSeatNumber: defaultSeat,
      isCustom: customVal != null,
    });
  }

  return layout;
}

/**
 * Deterministic one-to-one assignment of seated jurors to grid spaces.
 *
 * Jurors only store an effective seat number, and custom renumbering can
 * leave two spaces sharing a number mid-edit. Matching each space against
 * "any juror with that number" then either stacks both jurors in one space
 * or moves the wrong juror when a space is renumbered. Instead, spaces claim
 * jurors in display order and every juror is claimed at most once — the grid
 * and the renumbering action both use this, so the juror shown in a space is
 * exactly the one that follows its number.
 *
 * @param {Array<{spaceNumber: number, seatNumber: number}>} layout
 * @param {Array} jurors
 * @param {'jury-box'|'alternates'} mode
 * @returns {Map<number, Object|null>} spaceNumber -> juror (or null if empty)
 */
export function assignJurorsToLayout(layout, jurors, mode = 'jury-box') {
  const seatField = mode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';
  const discardedField = mode === 'jury-box' ? 'discarded' : 'alternateDiscarded';

  const byNumber = new Map();
  for (const juror of jurors) {
    const seat = juror[seatField];
    if (seat != null && seat > 0 && !juror[discardedField]) {
      if (!byNumber.has(seat)) byNumber.set(seat, []);
      byNumber.get(seat).push(juror);
    }
  }

  const bySpace = new Map();
  for (const space of layout) {
    const queue = byNumber.get(space.seatNumber);
    bySpace.set(space.spaceNumber, queue && queue.length ? queue.shift() : null);
  }
  return bySpace;
}

/** Effective seat numbers in display order. */
export function getSeatNumbers(caseData, mode = 'jury-box') {
  return getSeatLayout(caseData, mode).map((s) => s.seatNumber);
}

/**
 * The layout sorted by seat NUMBER rather than physical position. Batch
 * actions (fill, slide, restore) order by the numbering — seat 3 comes
 * before seat 4 no matter where they sit in the room.
 */
export function getSeatLayoutByNumber(caseData, mode = 'jury-box') {
  return [...getSeatLayout(caseData, mode)].sort(
    (a, b) => a.seatNumber - b.seatNumber,
  );
}

/**
 * Lowest-numbered open seat given the current jurors.
 * @returns {number|null} - null when the layout is full
 */
export function findFirstOpenSeat(caseData, jurors, mode = 'jury-box') {
  const seatField = mode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';
  const discardedField = mode === 'jury-box' ? 'discarded' : 'alternateDiscarded';

  const taken = new Set(
    jurors
      .filter((j) => !j[discardedField] && j[seatField] != null)
      .map((j) => j[seatField]),
  );

  for (const seat of getSeatLayoutByNumber(caseData, mode)) {
    if (!taken.has(seat.seatNumber)) {
      return seat.seatNumber;
    }
  }
  return null;
}

/** Whether a seat number exists in the current layout. */
export function isValidSeatNumber(caseData, mode, seatNumber) {
  return getSeatNumbers(caseData, mode).includes(seatNumber);
}
